import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { IoMdClose } from "react-icons/io";
import { FaFilter, FaSearch, FaSlidersH } from "react-icons/fa";
import { toast } from "react-toastify";

import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import NewsTicker from "../components/Home/Newtricker";
import { NewBanner } from "../components/Home/Banner";
import Pagination from "../utilis/Pagination";
import CartDrawer from "../components/CartDrawer";

import { getProducts } from "../redux/api/products";
import { addToCart } from "../redux/store/cartSlice";
import { categoryData } from "../data/links";
import api from "../utilis/api";
import banner from "../assets/fragrances.png";
import "./style.css";

const Fragrances = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const productsPerPage = 12;
  const { products, totalPages, total } = useSelector(
    (state) => state.products,
  );
  // URL params
  // ---------------- URL PARAMS ----------------
  const category = searchParams.get("category") || "";
  const vendor = searchParams.get("vendor") || "";
  const search = searchParams.get("search") || "";
  const tagsParam = searchParams.get("tags") || "";
  const pageParam = Number(searchParams.get("page")) || 1;

  const tags = useMemo(() => {
    return tagsParam ? tagsParam.split(",").map((t) => t.trim()) : [];
  }, [tagsParam]);

  // ---------------- STATE ----------------
  const [filters, setFilters] = useState({
    search,
    tags,
    vendor,
  });

  const [currentPage, setCurrentPage] = useState(pageParam);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [cartDrawerOpen, setCartDrawerOpen] = useState(false);
  const [vendors, setVendors] = useState([]);
  const [vendorsLoading, setVendorsLoading] = useState(false);

  const categoryOptions = useMemo(
    () =>
      categoryData
        .filter((item) =>
          ["Men", "Women", "Unisex", "Gifts", "new_year_sale", "best_deals"].includes(
            item.tag,
          ),
        )
        .map((item) => ({
          label: `${item.title}${item.subtitle ? ` ${item.subtitle}` : ""}`,
          value: item.tag,
        })),
    [],
  );

  const collectionTags = useMemo(
    () => ["New Arrivals", "Our Specials", "Top Sellers", "Best Sales"],
    [],
  );

  // ---------------- SYNC URL → STATE ----------------
  useEffect(() => {
    setFilters((prev) => ({
      ...prev,
      search,
      tags,
      vendor,
    }));

    setCurrentPage(pageParam);
  }, [search, tags, vendor, pageParam]);

  useEffect(() => {
    const fetchVendors = async () => {
      setVendorsLoading(true);
      try {
        const response = await api.get("/products/vendors");
        setVendors(response.data?.data || []);
      } catch (error) {
        console.error("Failed to fetch Shopify vendors:", error);
        setVendors([]);
      } finally {
        setVendorsLoading(false);
      }
    };

    fetchVendors();
  }, []);

  // ---------------- API PARAMS ----------------
  const apiParams = useMemo(() => {
    return {
      page: currentPage,
      limit: productsPerPage,
      ...(category && { category }),
      ...(filters.vendor && { vendor: filters.vendor }),
      ...(filters.search && { search: filters.search }),
      ...(filters.tags.length > 0 && { tags: filters.tags.join(",") }),
    };
  }, [currentPage, category, filters.vendor, filters.search, filters.tags]);

  // ---------------- FETCH (DEBOUNCED) ----------------
  useEffect(() => {
    const delay = setTimeout(() => {
      dispatch(getProducts(apiParams));
    }, 300);

    return () => clearTimeout(delay);
  }, [dispatch, apiParams]);

  // ---------------- URL UPDATE HELPER ----------------
  const updateURL = useCallback(
    (updatedFilters, page = 1, selectedCategory = category) => {
      const params = new URLSearchParams();

      if (selectedCategory) params.set("category", selectedCategory);
      if (updatedFilters.vendor) params.set("vendor", updatedFilters.vendor);
      if (updatedFilters.search) params.set("search", updatedFilters.search);
      if (updatedFilters.tags.length) {
        params.set("tags", updatedFilters.tags.join(","));
      }

      params.set("page", page);
      setSearchParams(params);
    },
    [category, setSearchParams],
  );

  // ---------------- FILTER CHANGE ----------------
  const handleFilterChange = useCallback(
    (field, value) => {
      setFilters((prev) => {
        const updated = { ...prev, [field]: value };
        updateURL(updated, 1);
        return updated;
      });

      setCurrentPage(1);
    },
    [updateURL],
  );
  const handleVendorChange = useCallback(
    (value) => {
      handleFilterChange("vendor", value);
    },
    [handleFilterChange],
  );

  const handleCategoryChange = useCallback(
    (value) => {
      updateURL(filters, 1, value);
      setCurrentPage(1);
    },
    [filters, updateURL],
  );

  // ---------------- RESET ----------------
  const handleResetFilters = useCallback(() => {
    const reset = {
      search: "",
      tags: [],
      vendor: "",
    };

    setFilters(reset);
    setCurrentPage(1);
    setSearchParams({});
  }, [setSearchParams]);

  // ---------------- CART ----------------
  const handleAddToCart = useCallback(
    (product) => {
      dispatch(addToCart(product));
      toast.success("Added to Cart Successfully");
      setCartDrawerOpen(true);
    },
    [dispatch],
  );

  // ---------------- NAVIGATION ----------------
  const handleProduct = useCallback(
    (id) => {
      navigate(`/product_description/${id}`);
    },
    [navigate],
  );

  // ---------------- SCROLL ----------------
  useEffect(() => {
    requestAnimationFrame(() => {
      const topValue = window.innerWidth < 500 ? 0 : 1060;
      window.scrollTo({ top: topValue, behavior: "smooth" });
    });
  }, [currentPage, vendor, category]);

  // ---------------- CATEGORY ----------------
  const selectedCategory = useMemo(() => {
    return (
      categoryData.find(
        (item) =>
          item?.tag?.toLowerCase() === category?.toLowerCase() ||
          item?.tag?.toLowerCase() === vendor?.toLowerCase(),
      ) || null
    );
  }, [category, vendor]);

  const renderFilterPanel = (isMobile = false) => (
    <aside className="w-full rounded-lg border border-gray-200 bg-white shadow-sm">
      <div className="border-b border-gray-200 px-5 py-4">
        <div className="flex items-center justify-between gap-3">
          <div>
            <p className="text-[12px] font-semibold uppercase tracking-[0.18em] text-gray-400">
              Refine
            </p>
            <h2 className="mt-1 flex items-center gap-2 font-Syne text-[20px] font-semibold text-gray-900">
              <FaSlidersH className="text-[15px] text-gray-500" />
              Filters
            </h2>
          </div>
          {(filters.search || filters.vendor || filters.tags.length || category) && (
            <button
              type="button"
              onClick={handleResetFilters}
              className="text-[13px] font-semibold text-gray-500 underline-offset-4 hover:text-gray-900 hover:underline"
            >
              Clear all
            </button>
          )}
        </div>
      </div>

      <form
        className="flex flex-col gap-6 px-5 py-5"
        onSubmit={(e) => e.preventDefault()}
      >
        <div>
          <label
            htmlFor={isMobile ? "mobile-product-search" : "product-search"}
            className="mb-2 block text-[13px] font-semibold uppercase tracking-[0.12em] text-gray-500"
          >
            Search
          </label>
          <div className="relative">
            <FaSearch className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[14px] text-gray-400" />
            <input
              id={isMobile ? "mobile-product-search" : "product-search"}
              type="search"
              placeholder="Search perfumes, notes, brands"
              value={filters.search}
              onChange={(e) => handleFilterChange("search", e.target.value)}
              className="h-11 w-full rounded-md border border-gray-200 bg-gray-50 pl-10 pr-3 text-[15px] text-gray-900 outline-none transition focus:border-gray-900 focus:bg-white"
            />
          </div>
        </div>

        <div>
          <p className="mb-3 text-[13px] font-semibold uppercase tracking-[0.12em] text-gray-500">
            Categories
          </p>
          <div className="grid grid-cols-2 gap-2 md:grid-cols-1">
            <label
              className={`flex h-10 cursor-pointer items-center justify-between rounded-md border px-3 text-[14px] transition ${
                !category
                  ? "border-gray-900 bg-gray-900 text-white"
                  : "border-gray-200 bg-gray-50 text-gray-700 hover:border-gray-400"
              }`}
            >
              <span>All fragrances</span>
              <input
                type="radio"
                name={isMobile ? "mobile-category" : "category"}
                value=""
                checked={!category}
                onChange={() => handleCategoryChange("")}
                className="sr-only"
              />
            </label>

            {categoryOptions.map((item) => (
              <label
                key={item.value}
                className={`flex h-10 cursor-pointer items-center justify-between rounded-md border px-3 text-[14px] transition ${
                  category.toLowerCase() === item.value.toLowerCase()
                    ? "border-gray-900 bg-gray-900 text-white"
                    : "border-gray-200 bg-gray-50 text-gray-700 hover:border-gray-400"
                }`}
              >
                <span className="truncate">{item.label}</span>
                <input
                  type="radio"
                  name={isMobile ? "mobile-category" : "category"}
                  value={item.value}
                  checked={category.toLowerCase() === item.value.toLowerCase()}
                  onChange={() => handleCategoryChange(item.value)}
                  className="sr-only"
                />
              </label>
            ))}
          </div>
        </div>

        <div>
          <label
            htmlFor={isMobile ? "mobile-brand-select" : "brand-select"}
            className="mb-2 block text-[13px] font-semibold uppercase tracking-[0.12em] text-gray-500"
          >
            Brand
          </label>
          <select
            id={isMobile ? "mobile-brand-select" : "brand-select"}
            value={filters.vendor}
            onChange={(e) => handleVendorChange(e.target.value)}
            className="h-11 w-full rounded-md border border-gray-200 bg-gray-50 px-3 text-[15px] text-gray-900 outline-none transition focus:border-gray-900 focus:bg-white"
            disabled={vendorsLoading}
          >
            <option value="">
              {vendorsLoading ? "Loading brands..." : "All brands"}
            </option>
            {vendors.map((brand) => (
              <option key={brand} value={brand}>
                {brand}
              </option>
            ))}
          </select>
        </div>

        <div>
          <p className="mb-3 text-[13px] font-semibold uppercase tracking-[0.12em] text-gray-500">
            Collections
          </p>
          <div className="flex flex-col gap-2">
            {collectionTags.map((tag) => (
              <label
                key={tag}
                className="flex cursor-pointer items-center gap-3 rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-[14px] text-gray-700 transition hover:border-gray-400"
              >
                <input
                  type="checkbox"
                  value={tag}
                  checked={filters.tags.includes(tag)}
                  onChange={(e) => {
                    const newTags = e.target.checked
                      ? [...filters.tags, tag]
                      : filters.tags.filter((t) => t !== tag);

                    handleFilterChange("tags", newTags);
                  }}
                  className="h-4 w-4 accent-gray-900"
                />
                <span>{tag}</span>
              </label>
            ))}
          </div>
        </div>
      </form>
    </aside>
  );

  return (
    <>
      <Helmet>
        <title>
          {selectedCategory
            ? selectedCategory.helment
            : "Aruhi Fragrances - Perfumes"}
        </title>
        <meta
          name="body_html"
          content={`Welcome to ${
            selectedCategory
              ? selectedCategory.helment
              : "Aruhi Fragrances - Perfumes"
          } page`}
        />
      </Helmet>

      <Navbar />

      <div className="bg-white w-full py-[1rem] px-[1rem] sm:px-[5rem] text-[16px] text-gray-500">
        <span className="text-gray-800">
          {" "}
          <Link to={"/"}>Home</Link>
        </span>{" "}
        {">"} {selectedCategory?.title} {selectedCategory?.subtitle}
      </div>

      {selectedCategory ? (
        <NewBanner image={selectedCategory.image} />
      ) : (
        <NewBanner image={banner} />
      )}

      <div className="py-[1rem] px-[1rem] sm:px-[5rem] text-[16px] text-gray-500 w-full ">
        {products && products.length > 0 ? (
          <>
            Showing{" "}
            <span className="font-semibold">
              {(currentPage - 1) * productsPerPage + 1}
            </span>{" "}
            to{" "}
            <span className="font-semibold">
              {(currentPage - 1) * productsPerPage + products.length}
            </span>{" "}
            of <span className="font-semibold">{total}</span> products
          </>
        ) : (
          "No products found"
        )}
        <br />
        <Link
          to={"/contact"}
          className="mt-[0.8rem] text-gray-500 hover:text-gray-900"
        >
          Didn't find your perfume? Tell us!
        </Link>
      </div>
      <div className="bg-white px-[1rem] py-[1rem] md:px-[4rem]">
        <div className="mb-4 flex items-center justify-between gap-3 md:hidden">
          <button
            type="button"
            onClick={() => setIsDrawerOpen(true)}
            className="flex h-11 items-center gap-2 rounded-md bg-gray-900 px-4 text-[15px] font-semibold text-white"
          >
            <FaFilter />
            Filters
          </button>
          <p className="text-right text-[13px] text-gray-500">
            Filters apply to the full catalog
          </p>
        </div>

        <div className="flex flex-col gap-6 md:flex-row md:items-start">
          {/* Desktop Sidebar */}
          <div className="sticky top-6 hidden w-[280px] shrink-0 md:block">
            {renderFilterPanel()}
          </div>
          
          {/* Products */}
          <div className="flex w-full flex-wrap justify-center md:flex-1">
            {products && products.length > 0 ? (
              products.map((product) => (
                <div
                  className="relative w-[300px] md:w-[300px] py-[1rem]  px-1 md:px-4 "
                  key={product.id}
                >
                  <div className="border border-gray-300 rounded-md bg-white h-full flex flex-col">
                    <div className="flex flex-col justify-center items-center gap-1 p-4">
                      <div className="w-full">
                        <h1 className="text-[16px] md:text-[16px] font-bold text-center truncate">
                          {product.title}
                        </h1>
                      </div>
                      <p className="text-[12px] sm:text-[16px] font-Outfit text-gray-600 text-center">
                        {product.vendor}
                        {/* {truncatebody_html(product.vendor, 10)} */}
                      </p>
                    </div>
                    <div className="flex justify-center items-center flex-shrink-0">
                      <img
                        src={product.image}
                        alt=""
                        className="w-[90%] h-[140px] md:h-[180px] object-cover cursor-pointer"
                        onClick={() => handleProduct(product.id)}
                      />
                    </div>
                    <div className="flex-1 flex flex-col p-4 gap-4">
                      <div className="flex gap-2 items-center justify-center">
                        <p className="text-[13px] sm:text-[20px] font-Syne font-semibold">
                          $ {product?.variants?.[0]?.price}
                        </p>
                        {product?.variants?.[0]?.compare_at_price && (
                          <p className="text-[13px] sm:text-[20px] font-Syne text-gray-400 font-semibold line-through">
                            $ {product?.variants?.[0]?.compare_at_price}
                          </p>
                        )}
                      </div>
                      <button
                        onClick={() => handleAddToCart(product)}
                        className="px-[0.4rem] sm:px-[1.2rem]  rounded-[33px] text-[#000]
         text-[14px] md:text-[18px] font-syne flex items-center justify-center h-[30px] md:h-[40px]   gap-2 bg-background-black"
                      >
                        Add to cart
                      </button>
                    </div>
                  </div>
                  {/* <button
                    onClick={() => handleWishlist(product)}
                    className="absolute top-[2rem] right-[2rem] w-[2rem] h-[2rem] rounded-full bg-green-200 flex items-center justify-center opacity-0 translate-y-[-10px] group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-300 ease-in-out hover:bg-green-300 hover:scale-110"
                  >
                    <FaRegHeart color="green" size={18} />
                  </button> */}
                </div>
              ))
            ) : (
              <p className="text-white text-center w-full">
                No products found.
              </p>
            )}
          </div>
        </div>

        {/* Pagination */}
        <div className="mt-8 flex justify-center">
          <Pagination
            totalItems={totalPages * productsPerPage}
            itemsPerPage={productsPerPage}
            currentPage={currentPage}
            onPageChange={(page) => setCurrentPage(page)}
          />
        </div>
      </div>
      <NewsTicker />
      {/* Mobile Drawer */}
      {isDrawerOpen && (
        <>
          <div
            className="fixed inset-0 z-40 bg-black/50"
            onClick={() => setIsDrawerOpen(false)}
          />
          <div
            className="fixed right-0 top-0 z-50 h-full w-[88%] max-w-sm overflow-y-auto bg-white p-4 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-4 flex justify-end">
              <button
                type="button"
                onClick={() => setIsDrawerOpen(false)}
                className="flex h-10 w-10 items-center justify-center rounded-full border border-gray-200 text-2xl text-gray-700"
                aria-label="Close filters"
              >
                <IoMdClose />
              </button>
            </div>
            {renderFilterPanel(true)}
          </div>
        </>
      )}
      {cartDrawerOpen && (
        //  drawer
        <CartDrawer onClose={() => setCartDrawerOpen(false)} />
      )}
      {/* Footer */}
      <Footer />
    </>
  );
};

export default Fragrances;
